import { useCallback, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { failureError, invalidError, notice } from '@/components/pbo/notice'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { FETCH_ALL_MESSAGES } from '@/hooks/useFetchAllRows'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import Loader from '@/components/content/Loader'
import { canSelectShipper, defaultShipper, ShipperScope, scopedShipperCode, shipperScopeOf, withCoMingle } from '@/components/system/shipperScope'
import { scopeSelectOptions } from '@/components/system/shipperScopeOptions'
import { useCoMingleShipperScope } from '@/components/system/useCoMingleShipperScope'
import { DOWNLOAD_REASON_EMPTY_MESSAGE } from '@/utils/consts'
import { ERP_INBOUNDS_EXCEL_URL } from '@/services/inbound/erpInbound/apis'
import {
  useConfirmErpInbounds,
  useDeleteErpInbounds,
  useGetErpInboundShipperOptions,
  useGetErpInbounds,
  useSaveErpInbounds,
  ErpInboundsCondition,
  erpRowKey,
} from '@/services/inbound/erpInbound/useErpInboundQueries'
import { ErpInboundItem, GetErpInboundsReq } from '@/services/inbound/erpInbound/types'
import { GRID_CDS, MESSAGES, ORD_OPTION_NUMBER_LIMIT, ORDER_TYPE_OPTIONS, REASON_DETAIL_MIN_LENGTH, TRANSPORT_TYPE_OPTIONS } from '@/pages/inbound/erpInbound/constants'
import { ErpInboundSearchQuery, fromSearchQuery, RestoredErpInboundState, toSearchQuery } from '@/pages/inbound/erpInbound/searchQuery'
import { buildErpInboundSaveBody } from '@/pages/inbound/erpInbound/erpInboundSaveRequest'
import { excludeAllOption, toSelectOptions, useGetCombos } from '@/services/combo/useComboQueries'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'
import ErpInboundPageView, { type ActionCtx, type CellOption, type Option, type Row, type Wiring } from './handoff/page.generated'
import { usePermissionSlot } from '@/hooks/useMenuPermission'

/** 서버 응답(2.0 StandardResponse)의 meta.message 를 꺼낸다. 없으면 폴백 문구를 쓴다. */
const extractErrorMessage = (error: unknown, fallback: string): string =>
  (error as { response?: { data?: { meta?: { message?: string }; message?: string } } })?.response?.data?.meta?.message ||
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
  fallback

/** BE LocalDateTime('…T07:55:24') → 'YYYY-MM-DD HH:mm:ss'(종전 columnDefinitions.formatDateTime 과 같은 규칙). */
const formatDateTime = (value: string | undefined | null): string => (value ? value.slice(0, 19).replace('T', ' ') : '')

/**
 * API 행 → 뷰 행. 뷰(컨덕터 생성본)의 표 컬럼은 데모 컬럼 키(1.0 컬럼명)라 여기서 맞춘다.
 * 원본은 `__item` 으로 실어 확정·삭제 식별자는 편집값이 아닌 원본에서 뽑는다. key = 행 키(재조회·페이지 이동 뒤 체크 선택 안정).
 */
const toRow = (item: ErpInboundItem, shipperName: (code: string) => string): Row => ({
  // 행 키 = 중복 제거 키(id|anId) — 같은 id 에 AN 이 둘 조인되면 id 만으로는 두 행이 한 키라 하나만 체크해도 둘 다 선택된다(표가 키로 선택 행을 되찾는다).
  key: erpRowKey(item),
  __item: item,
  SPR_NM: shipperName(String(item.shipperCode ?? '')),
  EINDT: item.einDt ?? '',
  HSDAT: item.hsdAt ?? '',
  VFDAT: item.vfdat ?? '',
  DELVNO: item.delvNo ?? '',
  DELVSEQ: item.delvSeq ?? '',
  ORD_TYPE: item.orderType ?? '',
  TRS_TYPE: item.transportType ?? '',
  ZZ_SEXTWG: item.zzSextwg ?? '',
  ZZ_ZBISMT: item.zzZbismt ?? '',
  CHARG: item.charg ?? '',
  ZZ_SMATNR: item.zzSmatnr ?? '',
  ZZ_BARCODE: item.zzBarcode ?? '',
  skuId: item.skuId ?? '', // 1.0 화면관리 컬럼 코드(COLUMN_CD) 그대로 — 대문자로 바꾸면 그리드 설정이 안 붙는다(LOGISPRD-17094)
  MAKTX: item.maktx ?? '',
  ZZ_OPTION: item.zzOption ?? '',
  EINQTY: item.einQty,
  NETPR_1: item.netPr1,
  WAERS: item.waers ?? '',
  LIFNR: item.liFnr,
  LIFTX: item.liFtx,
  WH_NM: item.warehouseName ?? '',
  WH_AREA: item.warehouseArea ?? '',
  INS_DATE: formatDateTime(item.createdAt),
  INS_BY: item.createdBy ?? '',
  UPD_DATE: formatDateTime(item.updatedAt),
  UPD_BY: item.updatedBy ?? '',
  GENDER: item.gender ?? '',
  NAME_GP: item.nameGp ?? '',
  ZZ_RFID: item.zzRfid ?? '',
})

/** 뷰 행(편집 셀 반영 뒤) → API 행. 편집 가능한 5개 컬럼만 되돌린다. 빈 문자열도 편집값이다(지운 것). */
const fromRow = (row: Row): ErpInboundItem | null => {
  const item = row.__item as ErpInboundItem | undefined
  if (!item) return null
  return {
    ...item,
    delvNo: typeof row.DELVNO === 'string' ? row.DELVNO : item.delvNo,
    orderType: typeof row.ORD_TYPE === 'string' ? row.ORD_TYPE : item.orderType,
    transportType: typeof row.TRS_TYPE === 'string' ? row.TRS_TYPE : item.transportType,
    warehouseName: typeof row.WH_NM === 'string' ? row.WH_NM : item.warehouseName,
    warehouseArea: typeof row.WH_AREA === 'string' ? row.WH_AREA : item.warehouseArea,
  }
}
/** 편집 셀 셀렉트 옵션(DS 계약상 value 는 문자열) — 옵션 슬롯은 렌더마다 불리므로 모듈에서 한 번만 만든다(#773 봇). */
const ORD_TYPE_CELL_OPTIONS: CellOption[] = ORDER_TYPE_OPTIONS.map((o) => ({ value: String(o.value), label: o.label }))
const TRS_TYPE_CELL_OPTIONS: CellOption[] = TRANSPORT_TYPE_OPTIONS.map((o) => ({ value: String(o.value), label: o.label }))

const str = (v: unknown): string => (typeof v === 'string' ? v : '')
const originals = (ctx: ActionCtx): ErpInboundItem[] => ctx.selected.map((r) => r.__item as ErpInboundItem | undefined).filter((it): it is ErpInboundItem => !!it)

/**
 * EGR01 일반입고(ERP) — 1.0 EGR01 을 pbo-core 로 재구현한 화면. (LOGISPRD-11243)
 *
 * 겉(조회영역·표·확인창·엑셀 사유 모달)은 컨덕터 뷰(`./handoff/page.generated`, 재핸드오프 때 통째로 갱신)가 그리고,
 * 이 파일은 옛 훅·API·세션/URL 유지·잠금을 그대로 두고 그 뷰에 배선(Wiring)으로 넘긴다(컴포넌트 모드, 컨덕터 문서 51).
 *
 * 데모 대비 의도적 이탈(근거는 각 지점 주석):
 * - 입고유형·배송유형 옵션은 데모 목록이 아니라 moms-be enum 을 따른다(constants.ts 주석).
 * - 화주명 옵션은 데모의 고정 4개가 아니라 `POST /api/v1/get-shippers` 전건이다.
 * - 저장은 선택한 행만 보낸다(1.0 EGR01 파리티).
 */
function ErpInboundScreen({ coMingle }: Readonly<{ coMingle: boolean }>) {
  // 마운트 스냅샷은 useRef 로 적는다 — useMemo 는 캐시라 React 가 언제든 버릴 수 있다(빈 의존성은 "한 번만" 의 보증이 아니다).
  // 화주 잠금(LOGISPRD-14653) — 통합 운영 화주(FREE_SHIPPERS) 외 계정은 화주명 옵션이 자기 화주 하나고, 복원·조회·엑셀 요청의
  // 화주도 자기 화주로 강제한다(1.0 FilterBar 규칙). 생성본에 화주명 disabled 슬롯이 없어 옵션 축소로 잠근다.
  //  재고통합 ON 이면 잠긴 29CM·INOOINT 는 자기 화주와 MUSINSA 를 고른다(LOGISPRD-15608, `withCoMingle`).
  const scopeRef = useRef<ShipperScope>()
  scopeRef.current ??= withCoMingle(shipperScopeOf(AuthStorage.user.get()), coMingle)
  const scope = scopeRef.current
  // 기본 화주 — 현재 세션 화주(shipper_code, 화주 전환 반영 LOGISPRD-14651). 재고통합 대상 계정은 MUSINSA 다(LOGISPRD-15718)
  const defaultShipperCode = defaultShipper(scope).shipperCode

  /**
   * 조회 상태를 메뉴 세션(sessionStorage)·URL 쿼리에 남겨 다른 메뉴를 다녀와도 유지한다(MOMS 공통 UX).
   * 복원은 URL 에서, 마운트 시 한 번만 읽는다.
   */
  const menuSession = useMenuOnSession() // reload 를 구조분해하지 않는다(unbound-method)
  const { parsedQs, replaceQuery } = useParsedQuery<ErpInboundSearchQuery>()
  const restoredRef = useRef<RestoredErpInboundState>()
  restoredRef.current ??= fromSearchQuery(parsedQs as Partial<ErpInboundSearchQuery>, defaultShipperCode)
  // 잠금 계정은 URL 에 고를 수 없는 화주가 남아 있으면 기본 화주로 복원한다 — 복원 조건이 마운트 자동 조회·엑셀에 그대로 쓰인다.
  if (scope.locked) restoredRef.current.params.shipperCode = scopedShipperCode(scope, restoredRef.current.params.shipperCode)
  const restored = restoredRef.current

  /**
   * 조회 조건 — null 이면 조회하지 않는다. 복원된 조건이 있으면 그 조건으로 켠다(결정 12): 캐시가 있으면(탭 복귀) 그대로 그리고,
   * 받다 만 캐시면 남은 구간을 이어 받고, 없으면 받는다(공용 훅 staleTime: Infinity).
   */
  const [queryParams, setQueryParams] = useState<ErpInboundsCondition | null>(restored.hasQuery ? restored.params : null)
  const [keyword, setKeyword] = useState(restored.keyword)
  /**
   * 편집 셀이 바꾼 행(뷰의 onRowsChange — 보이는 페이지 행 전체). 저장이 선택 행의 최신 값을 여기서 되찾고,
   * 뒤 구간이 붙어 표의 행이 바뀔 때 이 값을 받은 행 위에 덮어 받는 동안 한 편집이 남게 한다.
   */
  const editedRowsRef = useRef<ErpInboundItem[]>([])
  /** clearEdits 가 올린다 — 같은 응답으로 다시 받으면 받은 행 참조가 그대로라 rows 가 다시 계산되지 않아 비운 편집분이 화면에 남는다(#1019 리뷰). */
  const [editsVersion, setEditsVersion] = useState(0)
  /** 편집분을 버린다([조회]·저장·확정·삭제·초기화) — 화면의 행도 받은 값으로 되돌린다. 안 되돌리면 화면엔 편집값, 저장엔 서버값이 나간다. */
  const clearEdits = useCallback((): void => {
    editedRowsRef.current = []
    setEditsVersion((n) => n + 1)
  }, [])

  const { data: combos, isFetched: combosFetched } = useGetCombos(['WH_NM', 'DOWNLOAD_CD'], { errorToast: true })
  const warehouseNameOptions = useMemo(() => toSelectOptions(excludeAllOption(combos?.WH_NM)), [combos])
  const downloadReasonOptions = useMemo(() => toSelectOptions(combos?.DOWNLOAD_CD), [combos])

  // 전량 조회(LOGISPRD-15268) — BE 상한 1,000 구간을 이어 받아 뷰가 10,000건씩 나눈다. 실패·10만 건 상한 안내는 공용 훅이 띄운다.
  const { rows: items, totalCount, isFirstLoading, isFetchingRest, refetch, guardAction } = useGetErpInbounds(queryParams)
  /** 표에 있는 행을 행 키(id|anId)로 찾는 색인 — 저장이 선택 행마다 전량 배열을 훑지 않게 한다(#773 봇). */
  const itemsByKey = useMemo(() => new Map(items.map((it) => [erpRowKey(it), it])), [items])

  const { data: shipperOptions } = useGetErpInboundShipperOptions()
  // 옵션 슬롯은 렌더마다 불린다 — 응답이 바뀔 때만 목록을 만든다(#773 봇).
  const shipperDisplayOptions = useMemo(() => (shipperOptions ?? []).map((o) => ({ value: String(o.value), label: String(o.label) })), [shipperOptions])
  const shipperFilterOptions = useMemo(() => scopeSelectOptions(shipperDisplayOptions, scope), [shipperDisplayOptions, scope])
  const shipperNameByCode = useMemo(() => new Map((shipperOptions ?? []).map((o) => [o.value, o.label])), [shipperOptions])
  const getShipperName = useCallback((code: string) => shipperNameByCode.get(code) ?? code, [shipperNameByCode])
  // 받은 행 위에 편집분을 덮는다 — 뒤 구간이 붙어 표의 행이 바뀌어도 받는 동안 한 편집이 보이게. 편집은 뷰가 이미 그리므로 입력마다 다시 계산하지 않는다(ref).
  //  키는 중복 제거 키와 같은 id|anId — 같은 id 에 AN 이 둘 이상 조인된 행이 한 편집분으로 덮이면 확정·삭제가 엉뚱한 anId 로 나간다.
  const rows = useMemo(() => {
    const edited = new Map(editedRowsRef.current.map((it) => [erpRowKey(it), it]))
    // 화면 값만 편집분으로 덮고 __item 은 서버 원본을 둔다 — 확정·삭제 식별자(originals)가 고친 입고번호로 나가지 않게.
    return items.map((it) => ({ ...toRow(edited.get(erpRowKey(it)) ?? it, getShipperName), __item: it }))
    // editsVersion = clearEdits 신호. 편집분은 editedRowsRef 에서 읽는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, getShipperName, editsVersion])

  const { mutateAsync: confirmErpInbounds, isPending: isConfirming } = useConfirmErpInbounds()
  const { mutateAsync: deleteErpInbounds, isPending: isDeleting } = useDeleteErpInbounds()
  const { mutateAsync: saveErpInbounds, isPending: isSaving } = useSaveErpInbounds()
  // 저장·확정·삭제는 서로를 막는다(확정은 WMS 로 ASN 이 나가 되돌릴 화면 경로가 없다).
  const isMutating = isConfirming || isDeleting || isSaving
  const actionLock = useRef(false)
  const acquireAction = useCallback(() => {
    if (actionLock.current || isMutating) {
      notice.failure(MESSAGES.actionInProgress)
      return false
    }
    actionLock.current = true
    return true
  }, [isMutating])
  const releaseAction = useCallback(() => {
    actionLock.current = false
  }, [])

  /**
   * 확인창을 열기 전 공통 가드 — 목록을 받는 중·일부 실패·조회 실패면(guardAction) 또는 다른 액션이 진행 중이면(옛 acquireAction 자리) 열지 않는다.
   * 받다 만 표에서 확정·삭제·저장이 돌면 빠진 행이 대상에서 조용히 누락된다(#773 리뷰).
   *
   * "확인창이 떠 있는 동안 다른 확인창을 연다" 는 여기서 막지 않는다 — 브라우저에서 재현되지 않는다.
   * 실측(2026-09-18, 하네스 emit-harness demo-egr01): [확정] 확인창을 연 상태에서 [삭제] 버튼 자리를
   * 실제로 클릭해도 확인창은 1개 그대로였고, 그 좌표의 `document.elementFromPoint` 는 버튼이 아니라
   * `.ant-modal-wrap`(화면 전체를 덮는 fixed 레이어, z-index 2000)이었다. `core.Modal.confirm` 은
   * DS 가 antd Modal.confirm 에 centered 만 입혀 넘기는 것이라 기본 mask 가 뒤 화면 클릭을 삼킨다.
   * 열기 시점에 잠금을 잡으면 취소 시점을 알 슬롯이 없어 오히려 영구 차단이 된다.
   */
  const canOpenAction = useCallback((): boolean => {
    if (!guardAction()) return false
    if (actionLock.current || isMutating) {
      notice.failure(MESSAGES.actionInProgress)
      return false
    }
    return true
  }, [guardAction, isMutating])
  const { onDownloadExcel } = useExcelDownload<GetErpInboundsReq>({ fileNamePrefix: '일반입고_ERP', config: { method: 'post' }, errorToast: true })

  /** 같은 조건으로 다시 받는다 — 실패 알럿은 공용 훅이 띄운다. 저장·확정·삭제 뒤 목록을 서버값으로 되돌린다. */
  const reloadList = useCallback(async (): Promise<void> => {
    await refetch()
  }, [refetch])

  /** 조회 상태를 메뉴 세션과 URL 에 남긴다. 탭바가 갱신되도록 `reload()` 를 함께 부른다. */
  const persistSearch = useCallback(
    (next: { params: GetErpInboundsReq; keyword: string }) => {
      const searchQuery = toSearchQuery(next)
      MenuStorage.menuOnSession.setSearch(searchQuery)
      menuSession.reload()
      replaceQuery(searchQuery)
    },
    [menuSession, replaceQuery],
  )

  // ── 뷰 배선(Wiring) — 옛 핸들러의 본문을 슬롯에 잇는다. 확인창·모달·선택·검색어·페이저는 뷰가 가진다. ──
  /**
   * 배선 객체는 매 렌더 새로 만든다 — 뷰(생성본)가 렌더 첫머리에 props.wiring 을 모듈 홀더에 대입하므로
   * 최신 state 를 보려면 이 편이 맞다. useMemo 로 고정하면 첫 렌더의 클로저가 남아 조회해도 표가 갱신되지 않는다.
   * (골격 주석의 "같은 참조 유지" 는 엔트리 모드의 모듈 상수 배선을 가리킨다 — 컴포넌트 모드에는 해당하지 않는다.)
   */
  const wiring: Wiring = {
    // 화면관리 그리드 설정(LOGISPRD-17250) — 별표·[일괄 입력] 이 EGR06_HEAD 를 따른다. 표 id 는 columnLayout 키와 같다
    permission: usePermissionSlot('egr06', GRID_CDS),
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { erpTable: coreTableLayoutSlot(GRID_CDS.erpTable) },
    rows: {
      // total 없이 주면 뷰가 1만 건씩 페이지를 자르고 목록 검색을 전량에 건다(total 을 주면 서버 페이징 — 생성본 `ROWS_SERVERPAGED`)
      //  — 첫 구간·나머지 구간·재조회 중에는 받은 행 위에 로더를 띄워 표를 막는다(LOGISPRD-15390). 받는 만큼 '전체 N'·페이지 수가 늘어나는 게 진행 표시다(gl15 선례).
      ROWS: () => ({ rows, loading: isFirstLoading || isFetchingRest }),
    },
    actions: {
      /** [조회] — 뷰 조회영역 값(키 = filterKey): SPR_NM(화주코드)·DELVNO(입고번호 여러 건)·ORD_TYPE·TRS_TYPE('' = 전체). */
      search: (filters) => {
        const delvNo = Array.isArray(filters.DELVNO) ? (filters.DELVNO as string[]).filter(Boolean) : []
        if (delvNo.length > ORD_OPTION_NUMBER_LIMIT) {
          notice.invalid(MESSAGES.ordOptionNumberLimitExceeded)
          return
        }
        const shipperCode = scope.locked ? scopedShipperCode(scope, str(filters.SPR_NM)) : str(filters.SPR_NM)
        const params: GetErpInboundsReq = {
          ...(shipperCode ? { shipperCode } : {}),
          ...(delvNo.length ? { ordOptionNumbers: delvNo } : {}),
          ...(str(filters.ORD_TYPE) ? { orderType: str(filters.ORD_TYPE) } : {}),
          ...(str(filters.TRS_TYPE) ? { transportType: str(filters.TRS_TYPE) } : {}),
        }
        clearEdits()
        setKeyword('')
        persistSearch({ params, keyword: '' })
        // 조건 커밋 전에 refetch 하면 이전 조건으로 조회된다 — flushSync 로 먼저 커밋한다.
        flushSync(() => setQueryParams(params))
        // 같은 조건이어도 서버에 다시 묻는다(1.0) — 키가 같으면 캐시라 켜기만으로는 안 나간다. 조건이 바뀌어 새 키가 스스로 받는 중이면
        //  cancelRefetch: false 로 거기에 합류한다(취소하고 새로 띄우면 취소된 요청도 서버까지 가서 조회가 두 번 나간다).
        return refetch({ cancelRefetch: false }).then(() => undefined)
      },
      ROWS_rowsChange: (changed) => {
        editedRowsRef.current = changed.map(fromRow).filter((it): it is ErpInboundItem => !!it)
      },
      /** [확정] 확인 — 화주 단위 API. id 는 ArrivalNotice PK(anId). 실패는 throw(뷰가 확인창을 유지). */
      erpConfirmModal_ok: async (ctx) => {
        // 확인창이 떠 있는 사이 받기 상태가 바뀔 수 있다 — 열 때(canOpenAction)에 이어 실행 직전에도 본다(throw = 확인창 유지)
        if (!guardAction()) throw new Error(FETCH_ALL_MESSAGES.fetching)
        const selected = originals(ctx)
        const shipper = String(selected[0]?.shipperCode ?? queryParams?.shipperCode ?? '')
        const ids = selected.map((row) => row.anId).filter((anId): anId is number => anId != null)
        if (!shipper) throw new Error(MESSAGES.noShipperCode)
        if (ids.length !== selected.length) throw new Error(MESSAGES.unconfirmableRows(selected.length - ids.length))
        if (!acquireAction()) throw new Error(MESSAGES.actionInProgress)
        try {
          const confirmedIds = await confirmErpInbounds({ shipper, ids })
          clearEdits()
          await reloadList()
          return MESSAGES.confirmSucceeded(confirmedIds.length)
        } catch (error) {
          throw failureError(extractErrorMessage(error, MESSAGES.confirmFailed))
        } finally {
          releaseAction()
        }
      },
      /** [저장] 확인 — 선택한 행만, 편집 값은 onRowsChange 로 받은 최신 행에서 되찾는다. 부분 실패는 알럿만(false = 완료 토스트 없음). */
      erpSaveButton_ok: async (ctx) => {
        // 저장 버튼은 확인창(Modal.confirm) 분기라 opens 가드가 없다(컨덕터 계약) — 여기서 같은 검사를 한다.
        //  받는 중·일부 실패·조회 실패면 guardAction 이 안내하고, throw 로 확인창을 유지한다.
        if (!guardAction()) throw new Error(FETCH_ALL_MESSAGES.fetching)
        const selected = originals(ctx)
        if (!acquireAction()) throw new Error(MESSAGES.actionInProgress)
        const latest = new Map(editedRowsRef.current.map((it) => [erpRowKey(it), it]))
        const targets = selected.map((it) => latest.get(erpRowKey(it)) ?? itemsByKey.get(erpRowKey(it)) ?? it)
        try {
          const results = await saveErpInbounds(buildErpInboundSaveBody(targets))
          clearEdits()
          await reloadList()
          const failed = results.filter((result) => result.MSGTYP !== 'S')
          if (failed.length > 0) {
            notice.failure(MESSAGES.saveFailed(failed.length, results.length, failed[0].MESSAGE))
            return false
          }
          return MESSAGES.saveSucceeded(results.length)
        } catch (error) {
          throw failureError(extractErrorMessage(error, MESSAGES.saveRequestFailed))
        } finally {
          releaseAction()
        }
      },
      /** [삭제] 확인 — BE 는 입고번호(DELVNO) 단위로 지운다(같은 입고번호의 선택하지 않은 행도 함께, 문구가 알린다). */
      erpDeleteConfirm_ok: async (ctx) => {
        if (!guardAction()) throw new Error(FETCH_ALL_MESSAGES.fetching)
        const selected = originals(ctx)
        if (!acquireAction()) throw new Error(MESSAGES.actionInProgress)
        try {
          const result = await deleteErpInbounds({
            data: selected.map((row) => ({
              DELVNO: row.delvNo,
              EINDT: row.einDt,
              SPR_NM: String(row.shipperCode ?? ''),
              ORD_TYPE: String(row.orderType ?? ''),
              TRS_TYPE: row.transportType ?? undefined,
            })),
          })
          clearEdits()
          await reloadList()
          return MESSAGES.deleteSucceeded(result.successCount)
        } catch (error) {
          throw failureError(extractErrorMessage(error, MESSAGES.deleteFailed))
        } finally {
          releaseAction()
        }
      },
      /** 엑셀 사유 모달 [확인] — 사유코드·상세(5자 이상) 검증 뒤 마지막 조회 조건으로 내려받는다(사유는 쿼리로 나눠 보내고 BE 가 `{코드}_{상세}` 로 합친다). */
      erpDownloadModal_ok: async (ctx) => {
        const reason = typeof ctx.form?.reason === 'string' ? ctx.form.reason : ''
        const detail = typeof ctx.form?.detail === 'string' ? ctx.form.detail.trim() : ''
        if (!reason || detail.length < REASON_DETAIL_MIN_LENGTH) {
          throw invalidError(MESSAGES.downloadValidationFailed) // 실패는 throw — 다른 슬롯과 같은 계약(모달이 열린 채 남는다)
        }
        const reasonQuery = new URLSearchParams({ reasonCode: reason, detailedReason: detail })
        await onDownloadExcel(`${ERP_INBOUNDS_EXCEL_URL}?${reasonQuery}`, queryParams ?? {})
      },
    },
    options: {
      ERPSHIPPER_OPTIONS: () => shipperFilterOptions,
      ERPORDTYPE_OPTIONS: (): Option[] => ORDER_TYPE_OPTIONS,
      ERPTRSTYPE_OPTIONS: (): Option[] => TRANSPORT_TYPE_OPTIONS,
      ROWS_ORD_TYPE: () => ORD_TYPE_CELL_OPTIONS,
      ROWS_TRS_TYPE: () => TRS_TYPE_CELL_OPTIONS,
      ROWS_WH_NM: () => warehouseNameOptions,
      REASONRADIO_OPTIONS: () => downloadReasonOptions,
    },
    // 조회 기본값(기본 화주) · 진입 초기값(URL 복원 조건) — [초기화] 는 기본값으로 돌아간다.
    /** 화주 고정 계정은 화주명 칸을 잠근다(1.0 FilterBar 의 disabled, LOGISPRD-14949). 고를 화주가 둘 이상이면 연다(LOGISPRD-15608). 보이는 것만 잠그므로 값은 기본값·search 가 따로 고정한다 */
    filterDisabled: canSelectShipper(scope) ? undefined : { SPR_NM: true },
    filterDefaults: { erpFilterCard: (demo) => ({ ...demo, SPR_NM: defaultShipperCode, ORD_TYPE: '', TRS_TYPE: '' }) },
    filterInitial: {
      erpFilterCard: (defaults) => ({
        ...defaults,
        ...(restored.params.shipperCode ? { SPR_NM: restored.params.shipperCode } : {}),
        ...(restored.params.ordOptionNumbers?.length ? { DELVNO: restored.params.ordOptionNumbers } : {}),
        ...(restored.params.orderType ? { ORD_TYPE: restored.params.orderType } : {}),
        ...(restored.params.transportType ? { TRS_TYPE: restored.params.transportType } : {}),
      }),
    },
    useBusy: () => isMutating,
    opens: {
      // 모달을 열기 전 검증(옛 handleConfirm 이 확인창 앞에서 하던 것). 저장·삭제도 같은 가드를 지난다.
      erpDeleteButton: () => canOpenAction(),
      erpConfirmButton: (ctx) => {
        if (!canOpenAction()) return false
        const selected = originals(ctx)
        const shipper = selected[0]?.shipperCode ?? queryParams?.shipperCode
        if (!shipper) {
          notice.invalid(MESSAGES.noShipperCode)
          return false
        }
        const missing = selected.filter((it) => it.anId == null).length
        if (missing > 0) {
          notice.invalid(MESSAGES.unconfirmableRows(missing))
          return false
        }
        return true
      },
      erpExcelButton: () => {
        // 받는 중·일부 실패면 건수가 서버와 달라 0건 판정이 틀린다(안내는 guardAction 이 띄운다).
        if (!guardAction()) return false
        if (totalCount === 0) {
          notice.invalid(MESSAGES.excelNoRows)
          return false
        }
        if (combosFetched && downloadReasonOptions.length === 0) {
          notice.failure(DOWNLOAD_REASON_EMPTY_MESSAGE)
          return false
        }
        return true
      },
    },
    stateInitial: { listq: { 'table.q': restored.keyword } }, // 페이저는 뷰 기본(1페이지·10,000건 — MOMS 공통 규칙)
    /** 목록 검색어 확정 — 옛 handleKeywordCommit. 페이지 이동은 받은 행 안에서라 할 일이 없다. */
    onState: (s) => {
      if (s.listq['table.q'] === keyword) return
      setKeyword(s.listq['table.q'])
      persistSearch({ params: queryParams ?? {}, keyword: s.listq['table.q'] })
    },
    /** [초기화] — 남겨 둔 조회 조건까지 지운다(옛 handleFilterReset). reload() 를 빼면 탭 스트립이 옛 검색어를 되살린다. */
    onReset: () => {
      clearEdits()
      MenuStorage.menuOnSession.setSearch({})
      menuSession.reload()
      replaceQuery({})
      setQueryParams(null)
      setKeyword('')
    },
  }

  return <ErpInboundPageView wiring={wiring} />
}

/**
 * 재고통합 FF 첫 조회를 기다린 뒤 화면을 연다 — 화주 범위·URL 복원값은 마운트 첫 렌더에 한 번 만들어지므로
 * FF 값이 정해지기 전에 만들면 잠긴 29CM·INOOINT 의 MUSINSA 복원이 자기 화주로 덮인다(LOGISPRD-15608).
 */
function ErpInbound() {
  const { ready, coMingle } = useCoMingleShipperScope()
  if (!ready) return <Loader />
  return <ErpInboundScreen coMingle={coMingle} />
}

export default ErpInbound
